
import React, { useState, useRef, useEffect } from 'react';
import {
  Upload,
  X,
  FileText,
  CheckCircle2,
  Loader2,
  Save,
  AlertCircle,
  Eye,
  ZoomIn,
  ZoomOut,
  RotateCw,
  ExternalLink,
  Calendar
} from 'lucide-react';
import Modal from './Modal';
import { ClaudeService } from '../services/claudeService';
import { TicketDraft } from '../types';
import { ApiService } from '../services/api';
import { useToast } from '../context/ToastContext';

interface TicketUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  droppedFiles?: FileList | null; 
}

const TicketUploadModal: React.FC<TicketUploadModalProps> = ({ isOpen, onClose, onSuccess, droppedFiles }) => {
  const { addToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [drafts, setDrafts] = useState<TicketDraft[]>([]);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState<'upload' | 'review'>('upload');
  const [previewDraftId, setPreviewDraftId] = useState<string | null>(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewRotation, setPreviewRotation] = useState(0);
  const [previewPan, setPreviewPan] = useState({ x: 0, y: 0 });
  const panStartRef = useRef<null | { startX: number; startY: number; originX: number; originY: number }>(null);
  const touchStartRef = useRef<null | { startX: number; startY: number; originX: number; originY: number }>(null);
  const [verifyTarget, setVerifyTarget] = useState<{ draftId: string; field: string } | null>(null);
  const [mobilePreviewExpanded, setMobilePreviewExpanded] = useState(false);
  const [datePicker, setDatePicker] = useState<{ draftId: string; field: 'issue_date' | 'contravention_date' } | null>(null);
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});

  // Handle files dropped from parent component
  useEffect(() => {
    if (isOpen && droppedFiles && droppedFiles.length > 0) {
      handleFiles(droppedFiles);
    }
  }, [droppedFiles, isOpen]);

  // --- 1. File Handling ---

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const newDrafts: TicketDraft[] = Array.from(files).map(file => ({
      id: Math.random().toString(36).substring(2, 9),
      file,
      previewUrl: URL.createObjectURL(file),
      status: 'pending'
    }));

    setDrafts(prev => [...prev, ...newDrafts]);
    setStep('review');
    
    // Auto-start processing for pending files
    processQueue([...drafts, ...newDrafts]);
  };

  const processQueue = async (currentDrafts: TicketDraft[]) => {
    if (processing) return; 
    setProcessing(true);

    const draftsCopy = [...currentDrafts];

    for (let i = 0; i < draftsCopy.length; i++) {
      if (draftsCopy[i].status === 'pending') {
        draftsCopy[i].status = 'processing';
        setDrafts([...draftsCopy]);

        try {
          const result = await ClaudeService.extractTicketData(draftsCopy[i].file);
          draftsCopy[i].status = 'success';
          draftsCopy[i].data = result;
          // AI audit metadata is attached after the user saves verified fields.
        } catch (err: any) {
          console.error(`Failed to process ${draftsCopy[i].file.name}`, err);
          if (err?.code === 'bulk_upload_queued') {
            draftsCopy[i].status = 'queued';
            draftsCopy[i].error = "Your PCNs are being processed. We'll notify you when ready.";
          } else {
            draftsCopy[i].status = 'error';
            draftsCopy[i].error = err?.message || "We could not read this PCN clearly. Please upload a clearer image or enter the details manually.";
          }
        }
        
        setDrafts([...draftsCopy]);
      }
    }
    
    setProcessing(false);
  };

  const handleRetryExtraction = (id: string) => {
    const nextDrafts = drafts.map(d =>
      d.id === id ? { ...d, status: 'pending' as const, error: undefined } : d
    );
    setDrafts(nextDrafts);
    processQueue(nextDrafts);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const handleRemoveDraft = (id: string) => {
    setDrafts(prev => {
      const toRemove = prev.find(d => d.id === id);
      if (toRemove?.previewUrl) URL.revokeObjectURL(toRemove.previewUrl);
      return prev.filter(d => d.id !== id);
    });
    if (previewDraftId === id) setPreviewDraftId(null);
    if (drafts.length <= 1) setStep('upload');
  };

  // --- 2. Edit / Review Logic ---

  const handleFieldChange = (id: string, field: string, value: string | number) => {
    setDrafts(prev => prev.map(d => {
      if (d.id !== id || !d.data) return d;
      return {
        ...d,
        data: { ...d.data, [field]: value }
      };
    }));
  };

  // --- 3. Final Submission ---

  const handleSaveAll = async () => {
    const validDrafts = drafts.filter(d => d.status === 'success' && d.data);
    if (validDrafts.length === 0) {
      addToast("No valid tickets to save", 'error');
      return;
    }

    setSaving(true);
    try {
      const uploadedPaths: string[] = [];
      const payload = await Promise.all(
        validDrafts.map(async (d) => {
          const issueRaw = String(d.data!.issue_date || '').trim();
          const contraRaw = String(d.data!.contravention_date || '').trim();
          const issueIso = parseUkDateToIso(issueRaw);
          const contraIso = parseUkDateToIso(contraRaw);

          if (!issueIso) throw new Error(`Invalid Issue Date for ${d.file.name} (use DD/MM/YYYY or pick a date).`);
          if (!contraIso) throw new Error(`Invalid Contravention Date for ${d.file.name} (use DD/MM/YYYY or pick a date).`);

          const { path: scanPath } = await ApiService.uploadTicketScanAsset(d.file).catch((e: any) => {
            const msg = String(e?.message || e || 'Unknown error');
            throw new Error(`Failed to upload scan for ${d.file.name}: ${msg}`);
          });

          uploadedPaths.push(scanPath);
          return { ...d.data!, issue_date: issueIso, contravention_date: contraIso, document_url: scanPath };
        })
      );
      const result = await ApiService.createTicketBatch(payload).catch(async (e) => {
        await Promise.allSettled(uploadedPaths.map((p) => ApiService.deleteTicketEvidence(p)));
        throw e;
      });

      // Link verified tickets back to their server-side AI jobs. This is internal
      // telemetry/audit data, not customer metered billing.
      if (result.tickets && result.tickets.length > 0) {
        const normPcn = (raw: any) => String(raw || '').trim().toUpperCase().replace(/\s+/g, '');
        const ticketByPcn = new Map<string, any>();
        for (const t of result.tickets) {
          const key = normPcn((t as any).pcn_number);
          if (key) ticketByPcn.set(key, t);
        }

        const processedTicketIds = new Set<string>();
        for (const d of validDrafts) {
          const key = normPcn(d.data?.pcn_number);
          const ticket = ticketByPcn.get(key);
          if (!ticket?.id) continue;
          processedTicketIds.add(ticket.id);

          if (d.data?.ai_job_id) {
            try {
              await ClaudeService.attachTicketMetadata({
                ticketId: ticket.id,
                aiJobId: d.data.ai_job_id,
                fileHash: d.data.ai_file_hash,
                extractedData: d.data,
                lowConfidenceFields: d.data.low_confidence_fields || []
              });
            } catch (aiMetaErr) {
              console.warn('Failed to attach AI job metadata for ticket:', ticket.id, aiMetaErr);
              addToast(
                `Ticket ${ticket.pcn_number || ticket.id} was saved, but its AI audit metadata did not attach. Refresh and contact support if this persists.`,
                'warning'
              );
            }
          }
        }

        try {
          const trialResult = await ApiService.incrementFleetTrialProcessed(processedTicketIds.size);
          if (trialResult?.expired_by === 'pcn_limit') {
            addToast('Your 3-PCN trial snapshot is ready. Choose a plan or request a manually reviewed 10-PCN report to continue.', 'success');
          } else if (trialResult?.expired_by === 'time') {
            addToast('Your 7-day FleetMode trial has ended. Upgrade to continue processing PCNs.', 'warning');
          }
        } catch (trialErr) {
          console.warn('Failed to update FleetMode trial processed count:', trialErr);
          addToast('Tickets saved, but the trial counter did not update. Please refresh the dashboard or contact support.', 'warning');
        }
      }

      if (result.merged > 0) {
        addToast(`Saved ${result.created} new ticket(s), merged ${result.merged} duplicate(s)`, 'success');
      } else {
        addToast(`Successfully created ${result.created} ticket(s)`, 'success');
      }
      onSuccess();
      handleClose();
    } catch (err: any) {
      addToast("Failed to save batch: " + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    drafts.forEach(d => {
      try {
        if (d.previewUrl) URL.revokeObjectURL(d.previewUrl);
      } catch {
        // ignore
      }
    });
    setDrafts([]);
    setPreviewDraftId(null);
    setVerifyTarget(null);
    setMobilePreviewExpanded(false);
    setStep('upload');
    onClose();
  };

  // --- Renderers ---

  const isLowConfidence = (draft: TicketDraft, field: string) => {
    return draft.data?.low_confidence_fields?.includes(field);
  };

  const parseUkDateToIso = (raw: string): string | null => {
    const s = (raw || '').trim();
    if (!s) return null;

    // YYYY-MM-DD (allow 1-digit month/day)
    if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(s)) {
      const parts = s.split('-').map(Number);
      const yyyy = parts[0];
      const mm = parts[1];
      const dd = parts[2];
      if (!dd || !mm || !yyyy) return null;
      const d = new Date(Date.UTC(yyyy, mm - 1, dd, 0, 0, 0));
      if (!Number.isFinite(d.getTime())) return null;
      if (d.getUTCFullYear() !== yyyy || d.getUTCMonth() !== mm - 1 || d.getUTCDate() !== dd) return null;
      return d.toISOString();
    }

    // DD/MM/YYYY or DD-MM-YYYY or DD.MM.YYYY (allow 1-digit day/month; allow 2-digit years)
    const m = s.match(/^(\d{1,2})[\/\-. ](\d{1,2})[\/\-. ](\d{2}|\d{4})$/);
    if (m) {
      const dd = Number(m[1]);
      const mm = Number(m[2]);
      let yyyy = Number(m[3]);
      if (!dd || !mm || !yyyy) return null;
      if (yyyy < 100) yyyy = yyyy >= 80 ? 1900 + yyyy : 2000 + yyyy;
      const d = new Date(Date.UTC(yyyy, mm - 1, dd, 0, 0, 0));
      if (!Number.isFinite(d.getTime())) return null;
      if (d.getUTCFullYear() !== yyyy || d.getUTCMonth() !== mm - 1 || d.getUTCDate() !== dd) return null;
      return d.toISOString();
    }

    // DDMMYYYY or DDMMYY
    if (/^\d{6}$/.test(s) || /^\d{8}$/.test(s)) {
      const dd = Number(s.slice(0, 2));
      const mm = Number(s.slice(2, 4));
      let yyyy = Number(s.slice(4));
      if (yyyy < 100) yyyy = yyyy >= 80 ? 1900 + yyyy : 2000 + yyyy;
      if (!dd || !mm || !yyyy) return null;
      const d = new Date(Date.UTC(yyyy, mm - 1, dd, 0, 0, 0));
      if (!Number.isFinite(d.getTime())) return null;
      if (d.getUTCFullYear() !== yyyy || d.getUTCMonth() !== mm - 1 || d.getUTCDate() !== dd) return null;
      return d.toISOString();
    }

    // ISO or other parseable strings
    const d = new Date(s);
    return Number.isFinite(d.getTime()) ? d.toISOString() : null;
  };

  const formatIsoToUkDate = (raw: string): string => {
    const iso = parseUkDateToIso(raw);
    if (!iso) return raw;
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
  };

  const normalizeDateInput = (raw: string): string => {
    const iso = parseUkDateToIso(raw);
    return iso || raw;
  };

  const todayIsoUtc = () => {
    const now = new Date();
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0));
    return d.toISOString();
  };

  const isoToYyyyMmDd = (raw: string): string => {
    const iso = parseUkDateToIso(raw);
    if (!iso) return '';
    const d = new Date(iso);
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const yyyyMmDdToIso = (yyyyMmDd: string): string | null => {
    const v = (yyyyMmDd || '').trim();
    if (!v) return null;
    return parseUkDateToIso(v);
  };

  const isInvalidDate = (raw: string | undefined): boolean => {
    const s = String(raw || '').trim();
    if (!s) return false;
    return !parseUkDateToIso(s);
  };

  const openPreview = (draftId: string) => {
    setPreviewDraftId(draftId);
    setPreviewZoom(1);
    setPreviewRotation(0);
    setPreviewPan({ x: 0, y: 0 });
    setMobilePreviewExpanded(false);
    setDatePicker(null);
  };

  const verifyField = (draftId: string, field: string) => {
    openPreview(draftId);
    setVerifyTarget({ draftId, field });
  };

  const previewDraft = previewDraftId ? drafts.find(d => d.id === previewDraftId) : null;
  const previewIsPdf = !!previewDraft?.file?.type?.includes('pdf');
  const previewCanRenderImage = !!previewDraft?.file?.type?.includes('image');

  const clampZoom = (z: number) => Math.min(4, Math.max(0.5, z));
  const zoomIn = () => setPreviewZoom(z => clampZoom(Number((z + 0.25).toFixed(2))));
  const zoomOut = () => setPreviewZoom(z => clampZoom(Number((z - 0.25).toFixed(2))));
  const resetView = () => {
    setPreviewZoom(1);
    setPreviewRotation(0);
    setPreviewPan({ x: 0, y: 0 });
  };
  const rotate = () => setPreviewRotation(r => (r + 90) % 360);

  useEffect(() => {
    if (!verifyTarget) return;
    const key = `${verifyTarget.draftId}:${verifyTarget.field}`;
    const el = fieldRefs.current[key];
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (el as any).focus?.();
    } catch {
      // ignore
    }
  }, [verifyTarget]);

  useEffect(() => {
    if (!isOpen) setDatePicker(null);
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Batch Upload Tickets"
      maxWidth={step === 'review' ? 'max-w-6xl' : 'max-w-3xl'}
    >
      <div className="flex flex-col h-[70vh]">
        {step === 'review' && (
          <>
            {/* Mobile/Small-screen docked preview (keeps fields editable) */}
            {previewDraft && (
              <div className="lg:hidden px-6 pt-6">
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-950/40 border-b border-slate-200 dark:border-slate-800">
                    <div className="min-w-0">
                      <div className="text-xs text-slate-500 dark:text-slate-400">Preview</div>
                      <div className="text-sm font-bold text-slate-900 dark:text-white truncate">{previewDraft.file.name}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setMobilePreviewExpanded(v => !v)}
                        className="px-3 py-1.5 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                      >
                        {mobilePreviewExpanded ? 'Minimize' : 'Expand'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPreviewDraftId(null)}
                        className="px-3 py-1.5 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                      >
                        Hide
                      </button>
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={zoomOut} className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors inline-flex items-center gap-2">
                          <ZoomOut size={14} /> Zoom
                        </button>
                        <button type="button" onClick={zoomIn} className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors inline-flex items-center gap-2">
                          <ZoomIn size={14} /> Zoom
                        </button>
                        <button type="button" onClick={rotate} className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors inline-flex items-center gap-2">
                          <RotateCw size={14} /> Rotate
                        </button>
                        <button type="button" onClick={resetView} className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                          Reset
                        </button>
                        <div className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                          {Math.round(previewZoom * 100)}%
                        </div>
                      </div>
                      <a
                        href={previewDraft.previewUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors inline-flex items-center gap-2"
                        title="Open in new tab"
                      >
                        <ExternalLink size={14} />
                        Open
                      </a>
                    </div>
                    <div className={`${mobilePreviewExpanded ? 'h-[60vh]' : 'h-56'} w-full bg-slate-50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden`}>
                      {previewIsPdf ? (
                        <iframe title="PDF preview" src={previewDraft.previewUrl} className="w-full h-full" />
                      ) : previewCanRenderImage ? (
                        <div
                          className="w-full h-full relative overflow-hidden touch-none"
                          onMouseDown={(e) => {
                            panStartRef.current = { startX: e.clientX, startY: e.clientY, originX: previewPan.x, originY: previewPan.y };
                          }}
                          onMouseMove={(e) => {
                            if (!panStartRef.current) return;
                            const dx = e.clientX - panStartRef.current.startX;
                            const dy = e.clientY - panStartRef.current.startY;
                            setPreviewPan({ x: panStartRef.current.originX + dx, y: panStartRef.current.originY + dy });
                          }}
                          onMouseUp={() => { panStartRef.current = null; }}
                          onMouseLeave={() => { panStartRef.current = null; }}
                          onTouchStart={(e) => {
                            const t = e.touches[0];
                            if (!t) return;
                            touchStartRef.current = { startX: t.clientX, startY: t.clientY, originX: previewPan.x, originY: previewPan.y };
                          }}
                          onTouchMove={(e) => {
                            const t = e.touches[0];
                            if (!t || !touchStartRef.current) return;
                            const dx = t.clientX - touchStartRef.current.startX;
                            const dy = t.clientY - touchStartRef.current.startY;
                            setPreviewPan({ x: touchStartRef.current.originX + dx, y: touchStartRef.current.originY + dy });
                          }}
                          onTouchEnd={() => { touchStartRef.current = null; }}
                        >
                          <img
                            src={previewDraft.previewUrl}
                            alt="Preview"
                            className="absolute left-1/2 top-1/2 max-w-none select-none"
                            draggable={false}
                            style={{
                              transform: `translate(calc(-50% + ${previewPan.x}px), calc(-50% + ${previewPan.y}px)) scale(${previewZoom}) rotate(${previewRotation}deg)`,
                              transformOrigin: 'center'
                            }}
                          />
                        </div>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-500 dark:text-slate-400">
                          Preview not available for this file type.
                        </div>
                      )}
                    </div>
                    {!previewIsPdf && (
                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                        Tip: drag to pan • use zoom/rotate to verify flagged fields
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        
        {/* Step 1: Upload Zone */}
        {step === 'upload' && (
          <div 
            className="flex-1 flex flex-col items-center justify-center p-8 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 m-6 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800/80 hover:border-primary-400 dark:hover:border-primary-500"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <div className="p-4 bg-white dark:bg-slate-700 rounded-full shadow-sm mb-4">
              <Upload className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 dark:text-white">Drop files or folders here</h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6 text-center max-w-xs">
              Upload images (JPG, PNG) or PDFs. Our AI will classify and extract details automatically.
            </p>
            <button 
              onClick={() => fileInputRef.current?.click()}
              className="px-6 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              Select Files
            </button>
            <input 
              type="file" 
              multiple 
              accept="image/*,application/pdf"
              className="hidden" 
              ref={fileInputRef}
              onChange={(e) => handleFiles(e.target.files)}
            />
          </div>
        )}

        {/* Step 2: Review Table */}
        {step === 'review' && (
          <div className="flex flex-col h-full overflow-hidden">
             {/* Toolbar */}
             <div className="px-6 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-slate-900 shrink-0">
               <div className="flex items-center gap-2">
                 {processing ? (
                   <span className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 font-medium bg-primary-50 dark:bg-primary-900/20 px-3 py-1 rounded-full">
                     <Loader2 className="w-3 h-3 animate-spin" />
                     Processing {drafts.filter(d => d.status === 'processing' || d.status === 'pending').length} files...
                   </span>
                 ) : (
                   <span className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 font-medium bg-green-50 dark:bg-green-900/20 px-3 py-1 rounded-full">
                     <CheckCircle2 className="w-3 h-3" />
                     Analysis Complete
                   </span>
                 )}
               </div>
               <button 
                 onClick={() => fileInputRef.current?.click()}
                 className="text-xs font-medium text-slate-500 hover:text-primary-600 dark:text-slate-400 dark:hover:text-primary-400 flex items-center gap-1"
               >
                 <Upload size={14} /> Add more
               </button>
             </div>

             {/* Scrollable List */}
             <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
               <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/50 dark:bg-slate-950">
               {drafts.map((draft) => (
                   <div
                     key={draft.id}
                     className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col md:flex-row gap-6 animate-in fade-in slide-in-from-bottom-2 ${
                       previewDraftId === draft.id ? 'ring-2 ring-primary-500/40' : ''
                     }`}
                   >
                   {/* Preview */}
                   <div className="w-full md:w-32 h-32 shrink-0 bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden relative border border-slate-200 dark:border-slate-700">
                     {draft.file.type.includes('image') ? (
                       <img src={draft.previewUrl} alt="Preview" className="w-full h-full object-cover" />
                     ) : (
                       <div className="w-full h-full flex items-center justify-center text-slate-400 dark:text-slate-500">
                         <FileText size={32} />
                       </div>
                     )}

                     <button
                       type="button"
                       onClick={() => openPreview(draft.id)}
                       className="absolute bottom-2 left-2 right-2 px-2 py-1 bg-slate-900/80 text-white text-[10px] font-bold rounded-md hover:bg-slate-900 transition-colors flex items-center justify-center gap-1"
                       title="Open larger preview"
                     >
                       <Eye size={12} />
                       Preview
                     </button>
                     
                     {/* Status Overlay */}
                     {draft.status === 'processing' && (
                       <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                         <Loader2 className="w-8 h-8 text-white animate-spin" />
                       </div>
                     )}
                     {draft.status === 'error' && (
                       <div className="absolute inset-0 bg-red-500/10 flex items-center justify-center border-2 border-red-500">
                         <AlertCircle className="w-8 h-8 text-red-600" />
                       </div>
                     )}
                     {draft.status === 'queued' && (
                       <div className="absolute inset-0 bg-amber-500/10 flex items-center justify-center border-2 border-amber-400">
                         <Loader2 className="w-8 h-8 text-amber-600" />
                       </div>
                     )}
                   </div>

                   {/* Form / Data */}
                   <div className="flex-1 min-w-0">
                     <div className="flex items-start justify-between mb-4">
                        <div className="flex flex-col">
                          <h4 className="font-medium text-slate-800 dark:text-white truncate max-w-[200px]" title={draft.file.name}>{draft.file.name}</h4>
                          {draft.status === 'error' && (
                            <div className="space-y-2">
                              <p className="text-xs text-red-500">
                                {draft.error || 'We could not read this PCN clearly. Please upload a clearer image or enter the details manually.'}
                              </p>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleRetryExtraction(draft.id)}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                                  disabled={processing}
                                >
                                  <RotateCw size={12} />
                                  Try again
                                </button>
                                <button
                                  type="button"
                                  onClick={handleClose}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                                >
                                  <FileText size={12} />
                                  Enter manually
                                </button>
                              </div>
                            </div>
                          )}
                          {draft.status === 'queued' && (
                            <p className="mt-2 max-w-xl text-xs font-medium text-amber-700 dark:text-amber-300">
                              {draft.error || "Your PCNs are being processed. We'll notify you when ready."}
                            </p>
                          )}
                        </div>
                        <button onClick={() => handleRemoveDraft(draft.id)} className="text-slate-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400 p-1">
                          <X size={16} />
                        </button>
                     </div>

                     {draft.status === 'success' && draft.data && (
                       <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                         <div>
                           <div className="flex items-center justify-between gap-2 mb-1 h-4">
                             <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap" title="PCN Number">
                               PCN Number
                             </label>
                             {isLowConfidence(draft, 'pcn_number') ? (
                               <button type="button" onClick={() => verifyField(draft.id, 'pcn_number')} className="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap">
                                 Verify
                               </button>
                             ) : (
                               <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">Verify</span>
                             )}
                           </div>
                           <input 
                             type="text" 
                             value={draft.data.pcn_number}
                             onChange={(e) => handleFieldChange(draft.id, 'pcn_number', e.target.value)}
                             ref={(el) => {
                               fieldRefs.current[`${draft.id}:pcn_number`] = el;
                             }}
                             className={`w-full h-10 px-2 py-1.5 text-sm text-slate-900 dark:text-white border rounded bg-slate-50 dark:bg-slate-800 font-mono focus:ring-2 focus:bg-white dark:focus:bg-slate-900 outline-none ${
                               isLowConfidence(draft, 'pcn_number') 
                                 ? 'border-amber-400 ring-amber-100 dark:ring-amber-900/30 bg-amber-50 dark:bg-amber-900/20' 
                                 : 'border-slate-200 dark:border-slate-700 focus:ring-primary-100'
                             } ${verifyTarget?.draftId === draft.id && verifyTarget.field === 'pcn_number' ? 'ring-2 ring-primary-500/30' : ''}`}
                           />
                         </div>
                         <div>
                           <div className="flex items-center justify-between gap-2 mb-1 h-4">
                             <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap" title="VRM">
                               VRM
                             </label>
                             {isLowConfidence(draft, 'vrn') ? (
                               <button type="button" onClick={() => verifyField(draft.id, 'vrn')} className="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap">
                                 Verify
                               </button>
                             ) : (
                               <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">Verify</span>
                             )}
                           </div>
                           <input 
                             type="text" 
                             value={draft.data.vrn}
                             onChange={(e) => handleFieldChange(draft.id, 'vrn', e.target.value)}
                             ref={(el) => {
                               fieldRefs.current[`${draft.id}:vrn`] = el;
                             }}
                             className={`w-full h-10 px-2 py-1.5 text-sm text-slate-900 dark:text-white border rounded bg-slate-50 dark:bg-slate-800 font-mono font-bold uppercase focus:ring-2 focus:bg-white dark:focus:bg-slate-900 outline-none ${
                               isLowConfidence(draft, 'vrn') 
                                 ? 'border-amber-400 ring-amber-100 dark:ring-amber-900/30 bg-amber-50 dark:bg-amber-900/20' 
                                 : 'border-slate-200 dark:border-slate-700 focus:ring-primary-100'
                             } ${verifyTarget?.draftId === draft.id && verifyTarget.field === 'vrn' ? 'ring-2 ring-primary-500/30' : ''}`}
                           />
                         </div>
                         <div>
                           <div className="flex items-center justify-between gap-2 mb-1 h-4">
                             <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap" title="Amount (£)">
                               Amount (£)
                             </label>
                             {isLowConfidence(draft, 'amount') ? (
                               <button type="button" onClick={() => verifyField(draft.id, 'amount')} className="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap">
                                 Verify
                               </button>
                             ) : (
                               <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">Verify</span>
                             )}
                           </div>
                           <input 
                             type="number" 
                             value={draft.data.amount}
                             onChange={(e) => handleFieldChange(draft.id, 'amount', parseFloat(e.target.value))}
                             ref={(el) => {
                               fieldRefs.current[`${draft.id}:amount`] = el;
                             }}
                             className={`w-full h-10 px-2 py-1.5 text-sm text-slate-900 dark:text-white border rounded bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:bg-white dark:focus:bg-slate-900 outline-none ${
                               isLowConfidence(draft, 'amount') 
                                 ? 'border-amber-400 ring-amber-100 dark:ring-amber-900/30 bg-amber-50 dark:bg-amber-900/20' 
                                 : 'border-slate-200 dark:border-slate-700 focus:ring-primary-100'
                             } ${verifyTarget?.draftId === draft.id && verifyTarget.field === 'amount' ? 'ring-2 ring-primary-500/30' : ''}`}
                           />
                         </div>
                         <div>
                           <div className="flex items-center justify-between gap-2 mb-1 h-4">
                             <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap" title="Discount (£)">
                               Discount (£)
                             </label>
                             <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">Verify</span>
                           </div>
                           <input 
                             type="number" 
                             value={draft.data.discount_amount || ''}
                             onChange={(e) => handleFieldChange(draft.id, 'discount_amount', parseFloat(e.target.value))}
                             className="w-full h-10 px-2 py-1.5 text-sm text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:ring-primary-100 focus:bg-white dark:focus:bg-slate-900 outline-none"
                             placeholder="Optional"
                           />
                         </div>
                         <div>
                           <div className="flex items-center justify-between gap-2 mb-1 h-4">
                             <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap" title="Issue Date">
                               Issue Date
                             </label>
                             {isLowConfidence(draft, 'issue_date') ? (
                               <button type="button" onClick={() => verifyField(draft.id, 'issue_date')} className="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap">
                                 Verify
                               </button>
                             ) : (
                               <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">Verify</span>
                             )}
                           </div>
                           <div className="relative">
                             <input 
                               type="text" 
                               value={formatIsoToUkDate(String(draft.data.issue_date || ''))}
                               onChange={(e) => handleFieldChange(draft.id, 'issue_date', normalizeDateInput(e.target.value))}
                               placeholder="DD/MM/YYYY (or pick)"
                               inputMode="numeric"
                               ref={(el) => {
                                 fieldRefs.current[`${draft.id}:issue_date`] = el;
                               }}
                               className={`w-full h-10 px-2 py-1.5 pr-9 text-sm text-slate-900 dark:text-white border rounded bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:bg-white dark:focus:bg-slate-900 outline-none ${
                                 isInvalidDate(String(draft.data.issue_date || ''))
                                   ? 'border-red-400 ring-red-100 dark:ring-red-900/30 bg-red-50/40 dark:bg-red-900/10'
                                   : isLowConfidence(draft, 'issue_date')
                                     ? 'border-amber-400 ring-amber-100 dark:ring-amber-900/30 bg-amber-50 dark:bg-amber-900/20' 
                                     : 'border-slate-200 dark:border-slate-700 focus:ring-primary-100'
                               } ${verifyTarget?.draftId === draft.id && verifyTarget.field === 'issue_date' ? 'ring-2 ring-primary-500/30' : ''}`}
                             />
                             <button
                               type="button"
                               onClick={() => setDatePicker({ draftId: draft.id, field: 'issue_date' })}
                               className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400"
                               title="Pick date"
                             >
                               <Calendar size={14} />
                             </button>
                             {datePicker?.draftId === draft.id && datePicker.field === 'issue_date' && (
                               <div className="absolute right-0 mt-2 z-20 w-64 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-lg">
                                 <div className="flex items-center justify-between mb-2">
                                   <div className="text-xs font-bold text-slate-700 dark:text-slate-200">Pick issue date</div>
                                   <button
                                     type="button"
                                     onClick={() => setDatePicker(null)}
                                     className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400"
                                     title="Close"
                                   >
                                     <X size={14} />
                                   </button>
                                 </div>
                                 <input
                                   type="date"
                                   value={isoToYyyyMmDd(String(draft.data.issue_date || ''))}
                                   onChange={(e) => {
                                     const iso = yyyyMmDdToIso(e.target.value);
                                     handleFieldChange(draft.id, 'issue_date', iso || '');
                                     setDatePicker(null);
                                   }}
                                   className="w-full h-10 px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                 />
                                 <div className="mt-2 flex items-center justify-between">
                                   <button
                                     type="button"
                                     onClick={() => {
                                       handleFieldChange(draft.id, 'issue_date', todayIsoUtc());
                                       setDatePicker(null);
                                     }}
                                     className="text-xs font-bold text-primary-700 dark:text-primary-300 hover:underline"
                                   >
                                     Today
                                   </button>
                                   <div className="text-[10px] text-slate-400">Saves as UK date</div>
                                 </div>
                               </div>
                             )}
                           </div>
                         </div>
                         <div>
                           <div className="flex items-center justify-between gap-2 mb-1 h-4">
                             <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap" title="Contravention Date">
                               Contravention Date
                             </label>
                             {isLowConfidence(draft, 'contravention_date') ? (
                               <button type="button" onClick={() => verifyField(draft.id, 'contravention_date')} className="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap">
                                 Verify
                               </button>
                             ) : (
                               <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">Verify</span>
                             )}
                           </div>
                           <div className="relative">
                             <input
                               type="text"
                               value={formatIsoToUkDate(String(draft.data.contravention_date || ''))}
                               onChange={(e) => handleFieldChange(draft.id, 'contravention_date', normalizeDateInput(e.target.value))}
                               placeholder="DD/MM/YYYY (or pick)"
                               inputMode="numeric"
                               ref={(el) => {
                                 fieldRefs.current[`${draft.id}:contravention_date`] = el;
                               }}
                               className={`w-full h-10 px-2 py-1.5 pr-9 text-sm text-slate-900 dark:text-white border rounded bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:bg-white dark:focus:bg-slate-900 outline-none ${
                                 isInvalidDate(String(draft.data.contravention_date || ''))
                                   ? 'border-red-400 ring-red-100 dark:ring-red-900/30 bg-red-50/40 dark:bg-red-900/10'
                                   : isLowConfidence(draft, 'contravention_date')
                                     ? 'border-amber-400 ring-amber-100 dark:ring-amber-900/30 bg-amber-50 dark:bg-amber-900/20'
                                     : 'border-slate-200 dark:border-slate-700 focus:ring-primary-100'
                               } ${verifyTarget?.draftId === draft.id && verifyTarget.field === 'contravention_date' ? 'ring-2 ring-primary-500/30' : ''}`}
                             />
                             <button
                               type="button"
                               onClick={() => setDatePicker({ draftId: draft.id, field: 'contravention_date' })}
                               className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400"
                               title="Pick date"
                             >
                               <Calendar size={14} />
                             </button>
                             {datePicker?.draftId === draft.id && datePicker.field === 'contravention_date' && (
                               <div className="absolute right-0 mt-2 z-20 w-64 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-lg">
                                 <div className="flex items-center justify-between mb-2">
                                   <div className="text-xs font-bold text-slate-700 dark:text-slate-200">Pick contravention date</div>
                                   <button
                                     type="button"
                                     onClick={() => setDatePicker(null)}
                                     className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400"
                                     title="Close"
                                   >
                                     <X size={14} />
                                   </button>
                                 </div>
                                 <input
                                   type="date"
                                   value={isoToYyyyMmDd(String(draft.data.contravention_date || ''))}
                                   onChange={(e) => {
                                     const iso = yyyyMmDdToIso(e.target.value);
                                     handleFieldChange(draft.id, 'contravention_date', iso || '');
                                     setDatePicker(null);
                                   }}
                                   className="w-full h-10 px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                 />
                                 <div className="mt-2 flex items-center justify-between">
                                   <button
                                     type="button"
                                     onClick={() => {
                                       handleFieldChange(draft.id, 'contravention_date', todayIsoUtc());
                                       setDatePicker(null);
                                     }}
                                     className="text-xs font-bold text-primary-700 dark:text-primary-300 hover:underline"
                                   >
                                     Today
                                   </button>
                                   <div className="text-[10px] text-slate-400">Saves as UK date</div>
                                 </div>
                               </div>
                             )}
                           </div>
                          </div>
                         <div>
                           <div className="flex items-center justify-between gap-2 mb-1 h-4">
                             <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap" title="Stage">
                               Stage
                             </label>
                             {isLowConfidence(draft, 'stage') ? (
                               <button type="button" onClick={() => openPreview(draft.id)} className="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap">
                                 Verify
                               </button>
                             ) : (
                               <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">Verify</span>
                             )}
                           </div>
                           <input 
                             type="text" 
                             value={draft.data.stage}
                             onChange={(e) => handleFieldChange(draft.id, 'stage', e.target.value)}
                             ref={(el) => {
                               fieldRefs.current[`${draft.id}:stage`] = el;
                             }}
                             className={`w-full h-10 px-2 py-1.5 text-sm text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:ring-primary-100 focus:bg-white dark:focus:bg-slate-900 outline-none ${verifyTarget?.draftId === draft.id && verifyTarget.field === 'stage' ? 'ring-2 ring-primary-500/30' : ''}`}
                             placeholder="e.g. Notice to Owner"
                           />
                         </div>
                         <div>
                           <div className="flex items-center justify-between gap-2 mb-1 h-4">
                             <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap" title="Classification">
                               Classification
                             </label>
                             {isLowConfidence(draft, 'contravention_type') ? (
                               <button type="button" onClick={() => openPreview(draft.id)} className="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap">
                                 Verify
                               </button>
                             ) : (
                               <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">Verify</span>
                             )}
                           </div>
                           <select 
                             value={draft.data.contravention_type}
                             onChange={(e) => handleFieldChange(draft.id, 'contravention_type', e.target.value)}
                             ref={(el) => {
                               fieldRefs.current[`${draft.id}:contravention_type`] = el;
                             }}
                             className={`w-full h-10 px-2 py-1.5 text-sm text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:ring-primary-100 focus:bg-white dark:focus:bg-slate-900 outline-none ${verifyTarget?.draftId === draft.id && verifyTarget.field === 'contravention_type' ? 'ring-2 ring-primary-500/30' : ''}`}
                           >
                              <option>Parking Violation</option>
                              <option>Bus Lane</option>
                              <option>Moving Traffic</option>
                              <option>Congestion Charge</option>
                              <option>Low Emission Zone</option>
                           </select>
                         </div>
                         <div className="sm:col-span-2 xl:col-span-4">
                           <div className="flex items-center justify-between gap-2 mb-1 h-4">
                             <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap" title="Location">
                               Location
                             </label>
                             {isLowConfidence(draft, 'location') ? (
                               <button type="button" onClick={() => verifyField(draft.id, 'location')} className="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap">
                                 Verify
                               </button>
                             ) : (
                               <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">Verify</span>
                             )}
                           </div>
                           <input
                             type="text"
                             value={draft.data.location || ''}
                             onChange={(e) => handleFieldChange(draft.id, 'location', e.target.value)}
                             ref={(el) => {
                               fieldRefs.current[`${draft.id}:location`] = el;
                             }}
                             className={`w-full h-10 px-2 py-1.5 text-sm text-slate-900 dark:text-white border rounded bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:bg-white dark:focus:bg-slate-900 outline-none ${
                               isLowConfidence(draft, 'location')
                                 ? 'border-amber-400 ring-amber-100 dark:ring-amber-900/30 bg-amber-50 dark:bg-amber-900/20'
                                 : 'border-slate-200 dark:border-slate-700 focus:ring-primary-100'
                             } ${verifyTarget?.draftId === draft.id && verifyTarget.field === 'location' ? 'ring-2 ring-primary-500/30' : ''}`}
                             placeholder="e.g. FRANCIS STREET"
                           />
                         </div>
                       </div>
                     )}

                     {/* Skeleton for processing */}
                   {(draft.status === 'processing' || draft.status === 'pending' || draft.status === 'queued') && (
                     <div className="grid grid-cols-2 gap-4">
                       <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded animate-pulse"></div>
                       <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded animate-pulse"></div>
                       <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded animate-pulse"></div>
                       <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded animate-pulse"></div>
                     </div>
                   )}
                  </div>
                 </div>
                 ))}
               </div>

               {/* Desktop docked preview (keeps fields editable) */}
               <div className="hidden lg:flex w-[420px] xl:w-[520px] border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                 <div className="w-full flex flex-col">
                   <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40">
                     <div className="text-xs text-slate-500 dark:text-slate-400">Evidence viewer</div>
                     <div className="text-sm font-bold text-slate-900 dark:text-white truncate">
                       {previewDraft ? previewDraft.file.name : 'Select a flagged field to verify'}
                     </div>
                   </div>

                   <div className="p-4 flex-1 overflow-hidden">
                     {!previewDraft ? (
                       <div className="h-full flex items-center justify-center text-center text-slate-500 dark:text-slate-400 px-6">
                         Click “Verify” on a flagged field (or “Preview” on a file) to see the image here while editing the fields.
                       </div>
                     ) : (
                       <div className="flex flex-col h-full">
                         <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                           <div className="flex items-center gap-2">
                             <button type="button" onClick={zoomOut} className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors inline-flex items-center gap-2">
                               <ZoomOut size={14} /> Zoom
                             </button>
                             <button type="button" onClick={zoomIn} className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors inline-flex items-center gap-2">
                               <ZoomIn size={14} /> Zoom
                             </button>
                             <button type="button" onClick={rotate} className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors inline-flex items-center gap-2">
                               <RotateCw size={14} /> Rotate
                             </button>
                             <button type="button" onClick={resetView} className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                               Reset
                             </button>
                             <div className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                               {Math.round(previewZoom * 100)}%
                             </div>
                           </div>
                           <div className="flex items-center gap-2">
                             <a
                               href={previewDraft.previewUrl}
                               target="_blank"
                               rel="noreferrer"
                               className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors inline-flex items-center gap-2"
                             >
                               <ExternalLink size={14} />
                               Open
                             </a>
                             <button
                               type="button"
                               onClick={() => setPreviewDraftId(null)}
                               className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                             >
                               Hide
                             </button>
                           </div>
                         </div>

                         <div className="flex-1 bg-slate-50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
                           {previewIsPdf ? (
                             <iframe title="PDF preview" src={previewDraft.previewUrl} className="w-full h-full" />
                           ) : previewCanRenderImage ? (
                             <div
                               className="w-full h-full relative overflow-hidden cursor-grab active:cursor-grabbing"
                               onMouseDown={(e) => {
                                 panStartRef.current = { startX: e.clientX, startY: e.clientY, originX: previewPan.x, originY: previewPan.y };
                               }}
                               onMouseMove={(e) => {
                                 if (!panStartRef.current) return;
                                 const dx = e.clientX - panStartRef.current.startX;
                                 const dy = e.clientY - panStartRef.current.startY;
                                 setPreviewPan({ x: panStartRef.current.originX + dx, y: panStartRef.current.originY + dy });
                               }}
                               onMouseUp={() => { panStartRef.current = null; }}
                               onMouseLeave={() => { panStartRef.current = null; }}
                             >
                               <img
                                 src={previewDraft.previewUrl}
                                 alt="Preview"
                                 className="absolute left-1/2 top-1/2 max-w-none select-none"
                                 draggable={false}
                                 style={{
                                   transform: `translate(calc(-50% + ${previewPan.x}px), calc(-50% + ${previewPan.y}px)) scale(${previewZoom}) rotate(${previewRotation}deg)`,
                                   transformOrigin: 'center'
                                 }}
                               />
                             </div>
                           ) : (
                             <div className="w-full h-full flex items-center justify-center text-slate-500 dark:text-slate-400">
                               Preview not available for this file type.
                             </div>
                           )}
                         </div>
                         {!previewIsPdf && (
                           <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                             Tip: drag to pan • use zoom/rotate to verify flagged fields
                           </div>
                         )}
                       </div>
                     )}
                   </div>
                 </div>
               </div>
             </div>

             {/* Footer Actions */}
             <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0 flex items-center justify-between">
               <div className="text-xs text-slate-500 dark:text-slate-400">
                  {drafts.filter(d => d.status === 'success').length} verified, {drafts.filter(d => d.data?.low_confidence_fields?.length).length} flagged
               </div>
               <div className="flex gap-3">
                 <button 
                   onClick={handleClose}
                   className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-medium transition-colors"
                 >
                   Discard All
                 </button>
                 <button 
                   onClick={handleSaveAll}
                   disabled={saving || processing || drafts.every(d => d.status !== 'success')}
                   className="px-6 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                 >
                   {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save size={16} />}
                   Save Verified Tickets
                 </button>
               </div>
             </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TicketUploadModal;
